"use client"

import { createRef } from "react"
import clsx from "clsx"
import { useHover } from "usehooks-ts"

import { Accordion, AccordionPanel } from "@unlikelystudio/react-abstract-components"

import type { InferReturn, Nullish, PropsWithClassName } from "~/@types/generics"
import { AccordionPanelHead } from "~/components/ui/AccordionPanelHead"
import { title as accordionPanelHeadTitleStyle } from "~/components/ui/AccordionPanelHead/styles.css"
import InlineCta from "~/components/ui/InlineCta"
import { Link } from "~/components/ui/Link"
import SizeGuidePanel, { type SizeGuidePanelProps } from "~/components/ui/Panels/SizeGuide"
import { type serializeProductAccordions } from "~/components/ui/ProductHeader/components/ProductDetails/components/ProductDetailsAccordions/_data/serializer"
import RichText from "~/components/ui/RichText"
import Icon from "~/components/abstracts/Icon"
import type { TSizeGuideSlice } from "~/components/slices/SizeGuideSlice/types"
import { usePanel } from "~/managers/PanelManager"

import * as css from "./styles.css"

type PanelProps = ProductDetailsAccordionsProps["items"][number] & { index: number }

export type ProductDetailsAccordionsProps = PropsWithClassName<InferReturn<typeof serializeProductAccordions>> & {
  sizeGuideDisabled?: boolean
  sizeGuide?: Nullish<TSizeGuideSlice>
  productType: string | null
}

function PanelBody({ content }: { content: PanelProps["body"] }) {
  if (!content) return null

  if ("href" in content) {
    return <InlineCta className={css.content} {...content} />
  }

  if ("type" in content) {
    content
    return <RichText className={css.content} render={content} />
  }

  return null
}

export function ProductDetailsAccordions({
  className,
  items,
  sizeGuideDisabled,
  sizeGuide,
  productType,
}: ProductDetailsAccordionsProps) {
  return (
    <Accordion className={clsx(css.ProductDetailsAccordions, className)} autoHide={true}>
      {items
        .filter((item) => item?.head || item?.body)
        .map((item, index) => (
          <AccordionItem
            key={`panel_${index}`}
            index={index}
            sizeGuideDisabled={sizeGuideDisabled}
            sizeGuide={sizeGuide}
            productType={productType}
            {...item}
          />
        ))}
    </Accordion>
  )
}

function AccordionItem({
  head,
  body,
  type,
  index,
  sizeGuideDisabled,
  sizeGuide,
  productType,
}: ProductDetailsAccordionsProps["items"][number] &
  Pick<ProductDetailsAccordionsProps, "sizeGuideDisabled" | "sizeGuide" | "productType"> & { index: number }) {
  const headRef = createRef<HTMLAnchorElement | HTMLButtonElement>()
  const isHover = useHover(headRef)
  const { add } = usePanel()
  const openStylePanel = ({ productType, content }: SizeGuidePanelProps) => {
    add(<SizeGuidePanel productType={productType} content={content} />)
  }

  const isSizeGuide = type === "sizeGuide"
  const isTraceability = type === "traceability"

  if (type === "sizeGuide") {
    if (sizeGuideDisabled) {
      return null
    }
    return (
      <div className={css.panel} onClick={() => openStylePanel({ productType, content: sizeGuide })}>
        <div className={css.head}>
          <InlineCta active={isHover} withLine className={clsx(css.link, accordionPanelHeadTitleStyle)}>
            {head}
          </InlineCta>
          <Icon name="Arrow" width={12} direction="right" />
        </div>
      </div>
    )
  }

  if (type === "traceability") {
    return (
      <Link ref={headRef} href={body.href} className={css.panel} target="_blank">
        <div className={css.head}>
          <InlineCta active={isHover} withLine className={clsx(css.link, accordionPanelHeadTitleStyle)}>
            {head}
          </InlineCta>
          <Icon name="Arrow" width={12} direction="right" />
        </div>
      </Link>
    )
  }

  return (
    <AccordionPanel
      className={css.panel}
      headClassName={css.head}
      index={index}
      ariaLabels={{ close: head, open: head }}
      head={(isActive: boolean) => <AccordionPanelHead title={head} isActive={isActive} />}
      body={
        isTraceability || isSizeGuide ? null : (
          <div className={css.body}>
            <PanelBody content={body} />
          </div>
        )
      }
    />
  )
}
